import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn.js';

const CONTROL = cn(
  'w-full border bg-white px-3 text-sm text-n-900 transition-colors',
  'placeholder:text-n-400 disabled:cursor-not-allowed disabled:bg-n-50',
);

const BORDER_OK = 'border-n-200 focus:border-brand-500';
const BORDER_ERROR = 'border-error-600 focus:border-error-600';

export type FieldWrapperProps = {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: (props: { id: string; describedBy: string | undefined }) => ReactNode;
};

/**
 * Yorliq + izoh + xato uchun umumiy o'ram.
 *
 * `aria-describedby` avtomatik ulanadi: skrinrider foydalanuvchisi xatoni
 * maydonga o'tganda eshitadi, uni ko'z bilan qidirishi shart emas.
 */
export function FieldWrapper({ label, hint, error, required, children }: FieldWrapperProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-n-800">
          {label}
          {/*
            Yulduzcha faqat KO'RINISH uchun. Skrinrider majburiylikni
            `required` atributidan biladi — «yulduzcha» deb o'qish ortiqcha.
          */}
          {required && (
            <span aria-hidden className="ml-0.5 text-error-600">
              *
            </span>
          )}
        </label>
      )}

      {children({ id, describedBy })}

      {error && (
        <p id={errorId} className="text-sm text-error-600">
          {error}
        </p>
      )}
      {hint && !error && (
        <p id={hintId} className="text-sm text-n-600">
          {hint}
        </p>
      )}
    </div>
  );
}

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  label?: string;
  hint?: string;
  error?: string;
  /** Maydon ichidagi tugma yoki belgi (parolni ko'rsatish kabi). */
  suffix?: ReactNode;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, suffix, className, ...rest },
  ref,
) {
  return (
    <FieldWrapper label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy }) => (
        <div className="relative">
          <input
            ref={ref}
            id={id}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            required={required}
            className={cn(
              CONTROL,
              'h-10',
              error ? BORDER_ERROR : BORDER_OK,
              suffix && 'pr-10',
              className,
            )}
            {...rest}
          />
          {suffix && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-2">{suffix}</div>
          )}
        </div>
      )}
    </FieldWrapper>
  );
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string;
  hint?: string;
  error?: string;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, required, className, rows = 4, ...rest },
  ref,
) {
  return (
    <FieldWrapper label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy }) => (
        <textarea
          ref={ref}
          id={id}
          rows={rows}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          required={required}
          className={cn(CONTROL, 'py-2', error ? BORDER_ERROR : BORDER_OK, className)}
          {...rest}
        />
      )}
    </FieldWrapper>
  );
});

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string;
  hint?: string;
  error?: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  /** Bo'sh variant matni — «Hammasi», «Tanlanmagan». */
  placeholder?: string;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, required, options, placeholder, className, ...rest },
  ref,
) {
  return (
    <FieldWrapper label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy }) => (
        <select
          ref={ref}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(CONTROL, 'h-10', error ? BORDER_ERROR : BORDER_OK, className)}
          {...rest}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldWrapper>
  );
});

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: string;
  hint?: string;
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, className, ...rest },
  ref,
) {
  const id = useId();
  return (
    <div className="flex items-start gap-2">
      <input
        ref={ref}
        id={id}
        type="checkbox"
        className={cn('mt-0.5 size-4 border-n-200 text-brand-600', className)}
        {...rest}
      />
      <label htmlFor={id} className="text-sm text-n-800 select-none">
        {label}
        {hint && <span className="block text-xs text-n-600">{hint}</span>}
      </label>
    </div>
  );
});
